import { Link } from "react-router-dom";
import { ArrowLeft, FileText } from "lucide-react";
import { Logo } from "../components/Logo";
import { CONTACT_EMAIL } from "../data/content";

const SECTIONS = [
  {
    id: "quotes",
    title: "Quotes & estimates",
    body: [
      "You are welcome to discuss your requirements before any work begins — there is no charge and no obligation for an initial conversation.",
      "Where appropriate, I will provide a quote or estimate based on the agreed scope of work. Quotes clearly state the services included and any applicable charges, so you know what to expect before committing.",
      "If your requirements change, or additional work becomes necessary once a job is under way, I will discuss the changes and any additional costs with you and obtain your agreement before proceeding.",
    ],
  },
  {
    id: "scope",
    title: "Scope of work",
    body: [
      "Each job has an agreed scope describing the work to be completed, the expected deliverables and any relevant limitations.",
      "Work outside the original agreement may require a revised quote or a separate agreement. I will always aim to make sure we both understand exactly what is included before work starts.",
    ],
  },
  {
    id: "payment",
    title: "Pricing & payment",
    body: [
      "Prices are always communicated before work begins — hourly rates start from the figures shown on this website, and the total cost or basis of charging will be clear to you in advance.",
      "Payment arrangements — such as any deposit, hourly rate, fixed fee or payment timing — are agreed with you in advance for each job. Any additional charges are communicated and agreed before the additional work is undertaken.",
    ],
  },
  {
    id: "changes",
    title: "Changes, delays & cancellations",
    body: [
      "If you need to change or cancel an agreed job, please contact me as soon as possible so we can discuss the options.",
      "If a change affects the price, scope or estimated completion time, I will let you know and seek your agreement before continuing.",
      "Any cancellation arrangements or refunds will be consistent with what we have agreed and with applicable UK consumer law. For services agreed at a distance (for example, online or by phone), consumer law may give you a cancellation period — typically 14 days — although how this applies depends on the circumstances, such as whether you have asked for work to begin sooner. I will explain how this applies to your job when we agree the work.",
    ],
  },
  {
    id: "responsibilities",
    title: "Customer responsibilities",
    body: [
      "To help me help you, please provide accurate information about your requirements, make sure you are authorised to request changes to the relevant devices, accounts or data, and provide any access or permissions needed for the work.",
      "Where appropriate, please keep backups of important files before work begins. Before undertaking any work that could affect your data, I will explain the relevant precautions and any risks involved.",
    ],
  },
  {
    id: "data",
    title: "Data, confidentiality & security",
    body: [
      "I aim to handle your information responsibly and to respect confidentiality when carrying out agreed work. Your files, data and business information are treated as private.",
      "For details of how personal information is collected, used and protected on this website, please see the Privacy & Confidentiality page.",
      "While I take reasonable care, no IT service can promise absolute security, guaranteed data recovery or completely uninterrupted operation — I will always be honest with you about what is and is not possible.",
    ],
  },
  {
    id: "limitations",
    title: "Service limitations & results",
    body: [
      "I will make reasonable efforts to deliver all work professionally and in accordance with the agreed scope.",
      "The outcome of troubleshooting, repairs, automation and software work can depend on factors outside my control — for example the condition of existing hardware, the behaviour of third-party software, available system access, and the quality of information provided. Where such factors affect a job, I will explain the situation clearly and discuss the options with you.",
      "Nothing in these terms excludes or limits liability where the law does not allow it to be excluded or limited.",
    ],
  },
  {
    id: "rights",
    title: "Your consumer rights & complaints",
    body: [
      "These terms do not affect your statutory rights under UK law, including your rights under the Consumer Rights Act 2015 — for example, the right to expect services to be carried out with reasonable care and skill.",
      "If you have any concern about a service, an invoice or a completed job, please contact me first. I will always try to resolve issues professionally, fairly and quickly.",
    ],
  },
  {
    id: "acceptance",
    title: "Acceptance of terms",
    body: [
      "Before work begins, the applicable quote, agreed scope of work and payment arrangements should be accepted by you. Where appropriate, written confirmation by email will be used to record our agreement.",
      "If anything in these terms is unclear, just ask — I am happy to explain any part of them in plain English before you commit to anything.",
    ],
  },
];

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-canvas text-slate-100" data-testid="terms-page">
      <header className="sticky top-0 z-40 border-b border-cyan-500/15 bg-[#070B19]/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-5 py-4 sm:px-8">
          <Link to="/" data-testid="terms-logo" aria-label="Back to homepage">
            <Logo compact />
          </Link>
          <Link
            to="/"
            data-testid="terms-back-link"
            className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs font-medium text-slate-300 transition-colors hover:border-cyan-400/50 hover:text-cyan-300"
          >
            <ArrowLeft size={14} />
            Back to home
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-5 py-16 sm:px-8 md:py-20">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-cyan-400">Legal & terms</p>
        <h1 className="font-display mt-3 text-3xl font-bold tracking-tight text-slate-50 sm:text-4xl" data-testid="terms-title">
          Terms of Service
        </h1>
        <p className="mt-5 max-w-2xl text-sm leading-relaxed text-slate-400 sm:text-base">
          These terms set out how WA Digital Solutions — an independent IT support and digital solutions business run by
          Wojciech Arseniuk in Birmingham, UK — works with customers. The aim is simple: every job should start on clear
          ground, with both of us understanding the work, the price and the expectations before committing.
        </p>

        <div className="mt-8 rounded-2xl border border-cyan-500/20 bg-cyan-500/[0.05] p-6" data-testid="terms-intro-note">
          <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-cyan-300">
            <FileText size={14} />
            The short version
          </p>
          <p className="mt-3 text-sm leading-relaxed text-slate-300">
            Clear quote before work starts · agreed scope in writing · no extra charges without your agreement · your
            statutory rights are never affected · talk to me first if anything is not right.
          </p>
        </div>

        <nav className="mt-10 flex flex-wrap gap-2" aria-label="Page sections" data-testid="terms-nav">
          {SECTIONS.map((s) => (
            <a
              key={s.id}
              href={`#terms-${s.id}`}
              className="rounded-full border border-white/10 bg-white/[0.03] px-3.5 py-1.5 text-xs text-slate-400 transition-colors hover:border-cyan-400/40 hover:text-cyan-300"
            >
              {s.title}
            </a>
          ))}
        </nav>

        <div className="mt-10 space-y-8">
          {SECTIONS.map((s) => (
            <section
              key={s.id}
              id={`terms-${s.id}`}
              className="scroll-mt-24 rounded-2xl border border-white/10 bg-white/[0.03] p-6 md:p-7"
              data-testid={`terms-section-${s.id}`}
            >
              <h2 className="font-display text-lg font-semibold text-slate-50">{s.title}</h2>
              {s.body.map((p, i) => (
                <p key={i} className="mt-3 text-sm leading-relaxed text-slate-400">
                  {p}
                </p>
              ))}
              {s.id === "data" && (
                <Link
                  to="/privacy"
                  data-testid="terms-privacy-link"
                  className="mt-4 inline-flex items-center gap-2 rounded-full border border-cyan-500/40 px-5 py-2.5 text-sm font-semibold text-cyan-300 transition-colors hover:bg-cyan-500/10"
                >
                  Read the Privacy & Confidentiality page
                </Link>
              )}
              {s.id === "rights" && (
                <a
                  href={`mailto:${CONTACT_EMAIL}?subject=Service%20concern`}
                  data-testid="terms-contact-button"
                  className="mt-4 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-cyan-400 to-blue-600 px-5 py-2.5 text-sm font-semibold text-[#04101F] transition-transform duration-200 hover:scale-[1.03]"
                >
                  Contact me about a concern
                </a>
              )}
            </section>
          ))}
        </div>

        <p className="mt-10 border-t border-white/5 pt-6 text-xs leading-relaxed text-slate-500" data-testid="terms-footer-note">
          These terms apply to services provided by WA Digital Solutions as an independent UK business. They are written
          in good faith to set clear expectations and are not a substitute for legal advice. Last updated: October 2026.
        </p>
      </main>
    </div>
  );
}
